import { StrictMode } from 'react';
import { Root } from 'react-dom/client';
import ErrorPage from '~/containers/errors/ErrorPage';
import AppConfig from '~/utils/appconfig';

/**
 * Loads the application configuration, or renders a blocking error page
 * with a "Try again" action into `root` when it cannot be loaded.
 *
 * Fails closed: without its configuration the application cannot tell
 * which mode it runs in (a connector deployment would start in cloud
 * mode), so it must not start on defaults.
 *
 * @returns Whether the configuration loaded and start-up may continue.
 */
export async function loadConfigOrRenderError(
    root: Pick<Root, 'render'>
): Promise<boolean> {
    try {
        await AppConfig.initialize();
        return true;
    } catch (error) {
        console.error('Failed to load app configuration:', error);
        root.render(
            <StrictMode>
                <ErrorPage
                    status={503}
                    title="The application configuration could not be loaded."
                    // The transport's messages name the request and the
                    // status only, never a URL.
                    description={
                        'Check the network connection and try again. ' +
                        (error instanceof Error ? error.message : String(error))
                    }
                    retryable
                />
            </StrictMode>
        );
        return false;
    }
}
